class DimensionSlider {
  constructor(element) {
    this.element = element;
    this.paramName = element.dataset.filterParam;

    this.values = element.dataset.values
      .split(',')
      .map((value) => Number(value))
      .filter((value) => !Number.isNaN(value))
      .sort((a, b) => a - b);

    this.rangeMin = Number(element.dataset.min);
    this.rangeMax = Number(element.dataset.max);

    this.minInput = element.querySelector('[data-slider-min]');
    this.maxInput = element.querySelector('[data-slider-max]');
    this.progress = element.querySelector('[data-slider-progress]');
    this.currentMin = element.querySelector('[data-current-min]');
    this.currentMax = element.querySelector('[data-current-max]');
    this.summary = element.querySelector('[data-slider-summary]');
    this.applyButton = element.querySelector('[data-apply-range]');

    this.customMinParam = this.createCustomParamName('min');
    this.customMaxParam = this.createCustomParamName('max');

    if (
      !this.paramName ||
      !this.values.length ||
      !this.minInput ||
      !this.maxInput ||
      !this.progress ||
      !this.currentMin ||
      !this.currentMax ||
      !this.applyButton
    ) {
      return;
    }

    this.restoreValuesFromUrl();
    this.bindEvents();
    this.updateUI();
  }

  createCustomParamName(suffix) {
    const safeName = this.paramName.replace(/[^\w]/g, '_');
    return `dimension_${safeName}_${suffix}`;
  }

  bindEvents() {
    this.minInput.addEventListener('input', () => {
      this.preventCrossing('min');
      this.updateUI();
    });

    this.maxInput.addEventListener('input', () => {
      this.preventCrossing('max');
      this.updateUI();
    });

    this.applyButton.addEventListener('click', () => this.applyFilter());
  }

  restoreValuesFromUrl() {
    const params = new URLSearchParams(window.location.search);

    const customMinRaw = params.get(this.customMinParam);
    const customMaxRaw = params.get(this.customMaxParam);

    if (customMinRaw !== null) {
      const customMin = Number(customMinRaw);

      if (!Number.isNaN(customMin)) {
        this.minInput.value = this.clamp(customMin);
      }
    }

    if (customMaxRaw !== null) {
      const customMax = Number(customMaxRaw);

      if (!Number.isNaN(customMax)) {
        this.maxInput.value = this.clamp(customMax);
      }
    }

    if (customMinRaw !== null || customMaxRaw !== null) {
      this.preventCrossing('max');
      return;
    }

    const activeValues = params
      .getAll(this.paramName)
      .map((value) => Number(value))
      .filter((value) => !Number.isNaN(value))
      .sort((a, b) => a - b);

    if (!activeValues.length) return;

    this.minInput.value = this.clamp(activeValues[0]);
    this.maxInput.value = this.clamp(activeValues[activeValues.length - 1]);
  }

  clamp(value) {
    return Math.min(Math.max(value, this.rangeMin), this.rangeMax);
  }

  preventCrossing(changedInput) {
    let min = Number(this.minInput.value);
    let max = Number(this.maxInput.value);

    if (min <= max) return;

    if (changedInput === 'min') {
      this.minInput.value = max;
    } else {
      this.maxInput.value = min;
    }
  }

  getRange() {
    let min = Number(this.minInput.value);
    let max = Number(this.maxInput.value);

    if (Number.isNaN(min)) min = this.rangeMin;
    if (Number.isNaN(max)) max = this.rangeMax;

    min = this.clamp(min);
    max = this.clamp(max);

    if (min > max) {
      [min, max] = [max, min];
    }

    return { min, max };
  }

  updateUI() {
    const { min, max } = this.getRange();

    const total = this.rangeMax - this.rangeMin || 1;
    const left = ((min - this.rangeMin) / total) * 100;
    const right = 100 - ((max - this.rangeMin) / total) * 100;

    this.progress.style.left = `${left}%`;
    this.progress.style.right = `${right}%`;

    this.currentMin.textContent = min;
    this.currentMax.textContent = max;

    if (this.summary) {
      const isDefault = min <= this.rangeMin && max >= this.rangeMax;
      this.summary.textContent = isDefault ? '' : `${min}–${max} cm`;
    }
  }

  applyFilter() {
    const { min, max } = this.getRange();

    const url = new URL(window.location.href);
    const params = url.searchParams;

    params.delete(this.paramName);
    params.delete(this.customMinParam);
    params.delete(this.customMaxParam);
    params.delete('page');

    const isDefaultRange = min <= this.rangeMin && max >= this.rangeMax;

    if (isDefaultRange) {
      window.location.href = url.toString();
      return;
    }

    const selectedValues = this.values.filter((value) => {
      return value >= min && value <= max;
    });

    selectedValues.forEach((value) => {
      params.append(this.paramName, String(value));
    });

    params.set(this.customMinParam, String(min));
    params.set(this.customMaxParam, String(max));

    window.location.href = url.toString();
  }
}

function initDimensionSliders() {
  document.querySelectorAll('[data-dimension-slider]').forEach((element) => {
    if (element.dataset.initialized === 'true') return;

    element.dataset.initialized = 'true';
    new DimensionSlider(element);
  });
}

document.addEventListener('DOMContentLoaded', initDimensionSliders);
document.addEventListener('shopify:section:load', initDimensionSliders);